<template>
  <menu-layout
    :loading="loading"
    :progress="batch_progress"
    :progress-label="batch_progress != null ? $t(batch_progress_label) : ''"
    :progress-processed="batch_progress_processed"
    :progress-total="batch_progress_total"
    :progress-refreshing="batch_progress_refreshing"
  >
    <llm-analyse-modal
      v-if="llm_analyse_modal"
      :page-filters="filters"
      :selected-asset-ids="asset_ids_selected"
      @close_modal="close_llm_analyse_modal"
      @clear-selection="clearAssetSelection"
    />
    <run-model-filter-modal
      v-if="run_model_filter_modal"
      :page-filters="filters"
      :selected-asset-ids="asset_ids_selected"
      @close_modal="close_run_model_filter_modal"
      @clear-selection="clearAssetSelection"
    />
    <row>
      <col3>
        <filter-tree
            ref="filterTree"
            :initial_filter="filterTreeInitialFilter"
            @update="update"
            @load="load" :max_results="max_results"></filter-tree>
        <filter-save-load
            :filters="filters"
            :limit="limit"
            page-name="myassets"
            @load="loadSavedFilter"
        ></filter-save-load>
      </col3>
      <col9>
        <row>
          <col12 class="d-flex flex-wrap align-center mb-2 myassets-actions-row">
            <div
              v-if="can_run_auto_analyse"
              class="d-flex align-center action-with-queue"
            >
              <v-btn
                small
                outlined
                color="primary"
                :disabled="!filters_ready"
                @click="open_llm_analyse_modal"
              >
                {{ $t('myassets.llm_analyse') }}
              </v-btn>
              <my-assets-job-queue-button ref="llmJobQueue" category="llm" />
            </div>
            <div class="d-flex align-center action-with-queue">
              <v-btn
                small
                outlined
                color="primary"
                :disabled="!filters_ready"
                @click="open_run_model_filter_modal"
              >
                {{ $t('myassets.run_model_on_filter') }}
              </v-btn>
              <my-assets-job-queue-button ref="runModelJobQueue" category="run_model" />
            </div>
          </col12>
        </row>
        <row>
          <col12>
              <v-alert
                v-if="showAddValidationImagesBanner"
                v-model="showAddValidationImagesBanner"
                type="info"
                dense
                outlined
                dismissible
                class="mb-4"
              >
                <div class="d-flex flex-column flex-sm-row align-sm-center">
                  <span class="flex-grow-1">{{ $t('myassets.add_validation_images_hint') }}</span>
                  <v-btn
                    color="primary"
                    class="mt-2 mt-sm-0 ml-sm-4 flex-shrink-0"
                    @click="addSelectionToValidationSetFromBanner"
                  >
                    {{ $t('myassets.add_validation') }}
                  </v-btn>
                </div>
              </v-alert>
              <asset-grid ref="assetGrid" @reload="load" :items="items" v-on:remove_item="removeGridItem"
                        @update_selection_asset_ids="update_selection"
                        @updateSelectionAssetIds="update_selection">
                <template v-slot:menu>
                  <row>
                    <col2>
                      <dropdown-menu v-if="items.length > 0"
                                     :menu_items="menu_items"
                      ></dropdown-menu>
                    </col2>
                    <col5>
                      <div id="full-image-bbox-label" class="d-flex align-center" style="float:left; max-width: 480px; gap: 8px;">
                        <search-label-autocomplete
                            ref="fullImageBboxLabel"
                            v-if="items.length > 0"
                            style="max-width: 350px;"
                            :placeholder="$t('progress.search_field')"
                            v-model="full_image_bbox_label"
                            :clear_on_select="false"
                            label_type="box"
                            @label-selected="onFullImageBboxLabelSelect"
                            @cleared="full_image_bbox_label = null"
                        ></search-label-autocomplete>
                        <v-btn
                            id="add-full-image-bbox"
                            :disabled="!canAddFullImageBbox"
                            v-if="items.length > 0"
                            @click="add_full_image_bbox"
                        >
                          {{ $t('myassets.btn_add_full_image_bbox') }}
                        </v-btn>
                      </div>
                    </col5>
                  </row>
                </template>
              </asset-grid>
          </col12>
        </row>
      </col9>
    </row>
  </menu-layout>
</template>

<script>
import AssetGrid from "@/components/AssetGrid";
import FilterMixin from "@/mixins/FilterMixin";
import FilterTree from "@/components/asset_filter/FilterTree";
import MenuLayout from "@/components/layout/MenuLayout";
import DropdownMenu from "@/components/layout/DropdownMenu";
import MyAssetActionsMixin from "@/mixins/MyAssetActionsMixin";
import Col2 from "@/components/layout/Col2";
import Col5 from "@/components/layout/Col5";
import Row from "@/components/layout/Row";
import Col9 from "@/components/layout/Col9";
import Col12 from "@/components/layout/Col12";
import Col3 from "@/components/layout/Col3";
import FilterSaveLoad from "@/components/FilterSaveLoad";
import LlmAnalyseModal from "@/components/llm_analyse_modal/LlmAnalyseModal.vue";
import RunModelFilterModal from "@/components/run_model_modal/RunModelFilterModal.vue";
import MyAssetsJobQueueButton from "@/components/myassets/MyAssetsJobQueueButton.vue";
import MyAssetsJobsWebSocketMixin from "@/mixins/MyAssetsJobsWebSocketMixin.js";
import CurrentUserMixin from "@/mixins/CurrentUserMixin";
import SearchLabelAutocomplete from "@/components/SearchLabelAutocomplete.vue";
import { filterQueryHasTypeValue, flatFilters, toApiFilters } from "@/components/asset_filter/filterQuery";

const MY_ASSETS_DEFAULT_INITIAL_FILTER = [
  { type: 27, value: '' },
  { type: 23, value: 'Yes' },
  { type: 9, value: 'No' },
  { type: 11, value: 'No' },
  { type: 61, value: 'No' },
  { type: 7, value: '' },
];

/** Validated assets not yet in the validation set (same axes as default asset filters). */
const ADD_VALIDATION_IMAGES_FILTER_TEMPLATE = [
  { type: 27, value: '' },
  { type: 23, value: 'Yes' },
  { type: 9, value: 'Yes' },
  { type: 11, value: 'No' },
  { type: 61, value: 'No' },
  { type: 13, value: 'No' },
  { type: 7, value: '' },
];

export default {
  name: "MyAssets",
  data: function () {
    return {
      pendingAddValidationImagesFromQuery: false,
      showAddValidationImagesBanner: false,
      items: [],
      class_name: '',
      translations: {},
      class_name_relabel: '',
      toggle_all: true,
      max_results: false,
      current_filter: false,
      loading: false,
      filters: [],
      limit: 12,
      llm_analyse_modal: false,
      run_model_filter_modal: false,
      asset_ids_selected: [],
      full_image_bbox_label: null,
      assetLoadSeq: 0,
      base_colors: [
        [249, 65, 68],
        [243, 114, 44],
        [248, 150, 30],
        [67, 170, 139],
        [249, 132, 74],
        [249, 199, 79],
        [144, 190, 109],
        [77, 144, 142],
        [87, 117, 144],
        [39, 125, 161],
        [249, 65, 68],
        [243, 114, 44],
        [248, 150, 30],
        [67, 170, 139],
      ]
    }
  },
  created() {
    this.pendingAddValidationImagesFromQuery = this.hasAddValidationImagesQuery(this.$route);
  },
  computed: {
    filterTreeInitialFilter() {
      return this.pendingAddValidationImagesFromQuery ? false : MY_ASSETS_DEFAULT_INITIAL_FILTER;
    },
    filters_ready() {
      return flatFilters(this.filters || []).length > 0;
    },
    can_run_auto_analyse() {
      return this.is_admin || this.is_manager || this.is_global_admin;
    },
    menu_items () {
      let menu_items = []
      if(this.items.length > 0) {
        // Arrow wrappers keep `this` bound when DropdownMenu calls on_click().
        menu_items.push({'title': this.$t('myassets.correct'), 'on_click': () => this.validate_all()});
        menu_items.push({'title': this.$t('myassets.invalidate'), 'on_click': () => this.invalidate_all()});
        menu_items.push({'title': this.$t('myassets.add_validation'), 'on_click': () => this.add_to_validation_set()});
        menu_items.push({'title': this.$t('myassets.remove_validation'), 'on_click': () => this.remove_from_validation_set()});
        menu_items.push({'title': this.$t('myassets.add_full_image_bbox'), 'on_click': () => this.add_full_image_bbox()});
        menu_items.push({'title': this.$t('myassets.delete'), 'on_click': () => this.delete_assets()});
      }
      return menu_items
    },
    fullImageBboxLabelName() {
      if (this.full_image_bbox_label) {
        if (typeof this.full_image_bbox_label === 'object') {
          return this.full_image_bbox_label.name || null;
        }
        return this.full_image_bbox_label;
      }
      const search = this.$refs.fullImageBboxLabel;
      const selected = search?.search_label_selected;
      if (selected && selected.name) {
        return selected.name;
      }
      const acVal = search?.$refs?.ac?.selected_value;
      if (acVal && typeof acVal === 'object' && acVal.name) {
        return acVal.name;
      }
      if (typeof acVal === 'string' && acVal) {
        return acVal;
      }
      const typed = (search?.labelSearchInput || search?.$refs?.ac?.search || '').trim();
      return typed || null;
    },
    canAddFullImageBbox() {
      return this.asset_ids_selected.length > 0 && Boolean(this.fullImageBboxLabelName);
    },
    filters_includes_not_validated() {
      return filterQueryHasTypeValue(this.filters, 9, 'No');
    },
    filters_includes_validated() {
      return filterQueryHasTypeValue(this.filters, 9, 'Yes');
    },
    options() {
      return Object.values(this.translations);
    },
    nlToScientific() {
      let mapping = {}
      let values = Object.values(this.translations);
      let keys = Object.keys(this.translations);
      for (let i = 0; i < values.length; i++) {
        mapping[values[i]] = keys[i]
      }
      return mapping;
    }
  },
  components: {
    Col3,
    Col12,
    Col9,
    Row,
    Col2,
    Col5,
    DropdownMenu,
    MenuLayout,
    FilterTree,
    AssetGrid,
    FilterSaveLoad,
    LlmAnalyseModal,
    RunModelFilterModal,
    MyAssetsJobQueueButton,
    SearchLabelAutocomplete,
  },
  mixins: [FilterMixin, MyAssetActionsMixin, MyAssetsJobsWebSocketMixin, CurrentUserMixin],
  mounted() {
    this.$nextTick(() => {
      this.maybeApplyAddValidationImagesFromRoute(this.$route);
    });
  },
  watch: {
    '$route'(to) {
      this.maybeApplyAddValidationImagesFromRoute(to);
    },
    toggle_all(value) {
      for (let i = 0; i < this.items.length; i++) {
        this.items[i].checked = value;
      }
    },
  },
  methods: {
    hasAddValidationImagesQuery(route) {
      const raw = route.query['add-validation-images'];
      if (raw === undefined || raw === null) {
        return false;
      }
      const s = String(raw).toLowerCase();
      return s !== '0' && s !== 'false' && s !== '';
    },
    async maybeApplyAddValidationImagesFromRoute(route) {
      if (!this.hasAddValidationImagesQuery(route)) {
        return;
      }
      await this.applyAddValidationImagesFlow(route);
    },
    async applyAddValidationImagesFlow(route) {
      await this.$nextTick();
      if (!this.$refs.filterTree) {
        await this.$nextTick();
      }
      if (!this.$refs.filterTree) {
        return;
      }
      const limit = this.$refs.filterTree.limit != null ? this.$refs.filterTree.limit : 12;
      await this.$refs.filterTree.loadFilters(ADD_VALIDATION_IMAGES_FILTER_TEMPLATE, limit);
      const ftFilters = this.$refs.filterTree.filters;
      const ftLimit = this.$refs.filterTree.limit;
      this.filters = ftFilters;
      this.limit = ftLimit;
      await this.load(ftFilters, ftLimit);
      this.showAddValidationImagesBanner = true;
      this.$store.dispatch('snackbars/add_info_snack', {
        message: this.$t('myassets.add_validation_images_hint'),
        timeout: 10000,
      });
      const q = { ...route.query };
      delete q['add-validation-images'];
      await this.$router.replace({ path: route.path, query: q }).catch(() => {});
      this.pendingAddValidationImagesFromQuery = false;
    },
    async addSelectionToValidationSetFromBanner() {
      if (!this.asset_ids_selected || this.asset_ids_selected.length === 0) {
        await this.$store.dispatch('snackbars/add_failed_snack', this.$t('myassets.add_validation_images_select_first'));
        return;
      }
      await this.add_to_validation_set();
    },
    update(filters, limit) {
      this.filters = filters
      this.limit = limit
    },
    loadSavedFilter(filters, limit) {
      // Load saved filter into FilterTree and then load results
      if (this.$refs.filterTree) {
        this.$refs.filterTree.loadFilters(filters, limit);
      }
      // Update local state
      this.filters = filters;
      this.limit = limit;
      // Load the results
      this.load(filters, limit);
    },
    removeItem(asset_id) {
      let filteredItems = this.items.filter((image) => {
        return image.id !== asset_id
      });
      this.items = filteredItems;
    },
    update_selection(asset_ids) {
      this.asset_ids_selected = asset_ids
    },
    onFullImageBboxLabelSelect(label) {
      if (!label) {
        this.full_image_bbox_label = null;
      } else {
        this.full_image_bbox_label = label.name || label;
      }
    },
    async add_full_image_bbox() {
      await this.add_full_image_bboxes_to_selection(
        this.fullImageBboxLabelName,
        this.asset_ids_selected,
      );
    },
    removeGridItem(asset_id) {
      this.removeItem(asset_id)
    },
    applyAnnotationsWithLabels(items, labelsById) {
      items.forEach((item) => {
        try {
          item.annotations = item.relationships.annotations.map((annotation) => {
            let label = labelsById[annotation.label_id];
            if (!label) {
              let random_color = this.base_colors[Math.floor(Math.random() * this.base_colors.length)];
              annotation.coords = JSON.parse(annotation.coords);
              annotation.color_values = random_color;
              annotation.type = annotation.type === 1 ? 'box' : 'point';
              annotation.label = '?';
              annotation.conf = annotation.certainty === null ? 1 : annotation.certainty;
              annotation.show = true;
              annotation.selected = false;
              annotation.skip_zoom = true;
              return annotation;
            }
            annotation.coords = JSON.parse(annotation.coords);
            if (label.color) {
              annotation.color_values = label.color.split(',');
            } else {
              let random_color = this.base_colors[Math.floor(Math.random() * this.base_colors.length)];
              annotation.color_values = random_color;
            }
            annotation.type = annotation.type === 1 ? 'box' : 'point';
            annotation.label = label.name;
            annotation.conf = annotation.certainty === null ? 1 : annotation.certainty;
            annotation.show = true;
            annotation.selected = false;
            annotation.skip_zoom = true;
            return annotation;
          });
        } catch (error) {
          console.log(error);
          item.annotations = [];
        }
        item.checked = true;
        item.path = this.$api_host + '/' + item.static_path;
      });
    },
    open_llm_analyse_modal() {
      this.llm_analyse_modal = true;
    },
    close_llm_analyse_modal() {
      this.llm_analyse_modal = false;
    },
    open_run_model_filter_modal() {
      this.run_model_filter_modal = true;
    },
    close_run_model_filter_modal() {
      this.run_model_filter_modal = false;
    },
    clearAssetSelection() {
      if (this.$refs.assetGrid && this.$refs.assetGrid.clearSelection) {
        this.$refs.assetGrid.clearSelection();
      }
      this.asset_ids_selected = [];
    },
    isFilterQuery(value) {
      return Array.isArray(value)
        || (value && typeof value === 'object' && Array.isArray(value.groups));
    },
    resolveFilterParams(filters, limit) {
      if (this.isFilterQuery(filters)) {
        return {
          filters: toApiFilters(filters),
          limit: limit != null && limit !== false ? limit : this.resolveLimit(),
        };
      }
      if (this.isFilterQuery(this.filters)) {
        return { filters: toApiFilters(this.filters), limit: this.resolveLimit() };
      }
      const filterTree = this.$refs.filterTree;
      if (filterTree && typeof filterTree.apiFilters === 'function') {
        return {
          filters: filterTree.apiFilters(),
          limit: filterTree.limit != null ? filterTree.limit : 12,
        };
      }
      return { filters: [], limit: 12 };
    },
    resolveLimit() {
      if (this.limit != null && this.limit !== false) {
        return this.limit;
      }
      const filterTree = this.$refs.filterTree;
      return filterTree && filterTree.limit != null ? filterTree.limit : 12;
    },
    async load(filters, limit) {
      const resolved = this.resolveFilterParams(filters, limit);
      filters = resolved.filters;
      limit = resolved.limit;
      this.filters = filters;
      this.limit = limit;

      const loadGeneration = ++this.assetLoadSeq;
      this.items = [];
      this.loading = true
      try {
        const filterResult = await this.$api.asset.filter(filters, limit, { include_count: true });
        let items = filterResult;
        let bundledCount = null;
        if (filterResult && typeof filterResult === 'object' && Array.isArray(filterResult.items)) {
          items = filterResult.items;
          bundledCount = filterResult.count;
        }
        if (!Array.isArray(items)) {
          items = [];
        }
        if (bundledCount != null) {
          if (loadGeneration === this.assetLoadSeq) {
            this.max_results = bundledCount;
          }
        } else {
          this.$api.asset.count_filter(filters).then((count) => {
            if (loadGeneration === this.assetLoadSeq) {
              this.max_results = count;
            }
          });
        }

        if (!items || items.length === 0) {
          if (loadGeneration === this.assetLoadSeq) {
            this.max_results = 0;
            this.items = [];
          }
          return;
        }

        const idSet = new Set();
        items.forEach((item) => {
          ((item.relationships && item.relationships.annotations) || []).forEach((a) => {
            if (a.label_id) {
              idSet.add(a.label_id);
            }
          });
        });
        const labelIds = Array.from(idSet);
        let labelsById = {};
        if (labelIds.length > 0) {
          const labels = (await this.$api.label.fetchByIds(labelIds)) || [];
          labels.forEach((l) => {
            labelsById[l.id] = l;
          });
        }
        if (loadGeneration !== this.assetLoadSeq) {
          return;
        }
        this.applyAnnotationsWithLabels(items, labelsById);
        this.items = items;
      } finally {
        if (loadGeneration === this.assetLoadSeq) {
          this.loading = false;
        }
      }
    }
  },
}
</script>

<style scoped>
.myassets-actions-row {
  gap: 20px;
}

.action-with-queue {
  gap: 10px;
  padding-top: 6px;
  padding-right: 14px;
}
</style>
